import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Send, Lock, MessageSquare, Loader2, Paperclip, Image, FileText } from "lucide-react";
import axios from "axios";
import { ApiEndPoint } from "../services/apiUttiLities";
import socketService from "../services/socketService";
import AuthenticatedMedia, { AuthenticatedMediaLink } from "../components/AuthenticatedMedia";

/**
 * PrivateChatModal — Epic 12.2
 * Slide-in panel for private 1:1 DM chat.
 *
 * Props:
 *   isOpen        {boolean}
 *   onClose       {() => void}
 *   currentUser   {{ id, name, ... }}  — logged-in user
 *   targetUser    {{ id, name, ... }}  — the user to DM (e.g. reporter)
 *   incidentId    {string}             — for context label only
 *   incidentTitle {string}             — display title
 *   roomId        {string}             — optional override
 */
const PrivateChatModal = ({
  isOpen,
  onClose,
  currentUser,
  targetUser,
  incidentId,
  displayIncidentId,
  incidentTitle,
  roomId: passedRoomId,
}) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [roomId, setRoomId] = useState(null);
  
  // Resolve IDs consistently
  const myId = currentUser?.id || currentUser?._id;
  const otherId = targetUser?.id || targetUser?._id;
  
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Build canonical DM room ID (same logic as backend: DM-{incidentId}-{sorted ids})
  const buildRoomId = useCallback((a, b, incId) => {
    // Ensure incId is a valid, non-empty string that doesn't look like a placeholder
    const validIncId = (incId && String(incId) !== "undefined" && String(incId) !== "null") ? String(incId) : null;
    const prefix = validIncId ? `DM-${validIncId}-` : "DM-";
    return prefix + [String(a), String(b)].sort().join("_");
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  useEffect(() => {
    // If we don't have a passed room ID, we MUST have a valid incidentId to build one.
    // Otherwise we might land in a "DM-user1_user2" room which lacks isolation.
    if (!isOpen || !myId || !otherId || (!passedRoomId && !incidentId)) {
      return;
    }

    const room = passedRoomId || buildRoomId(myId, otherId, incidentId);
    setRoomId(room);

    // 1. Fetch history
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${ApiEndPoint}/api/dm/${encodeURIComponent(room)}/messages`, {
          params: { incidentId, otherUserId: otherId },
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` },
        });
        if (res.data.status) {
          const normalized = (res.data.data || []).map((m) => ({
            ...m,
            id: m._id,
            createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
          }));
          setMessages(normalized);
        }
      } catch (err) {
        console.error(`❌ [DM] History fetch failed for ${room}:`, err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();

    // 2. Join socket DM room
    socketService.emit("join_dm", { userId: myId, otherUserId: otherId, incidentId, room });

    // 3. Listen for new DM messages
    const handleNewDm = (msg) => {
      if (msg.room !== room) return;

      // Dispatch custom event to notify parent components of new message
      window.dispatchEvent(new CustomEvent("dm_message_received", { detail: msg }));

      const normalized = {
        ...msg,
        id: msg._id,
        createdAt: msg.createdAt ? new Date(msg.createdAt) : new Date(),
      };
      setMessages((prev) => {
        // 1. If we already have this message (by real ID), skip
        if (prev.some((m) => m.id && m.id === normalized.id)) return prev;

        // 2. If it's from me, find and remove the matching optimistic message
        if (normalized.senderId === myId) {
          // Find index of first optimistic message with matching text
          const optIndex = prev.findIndex(m => m._optimistic && m.text === normalized.text);
          if (optIndex !== -1) {
            const newArr = [...prev];
            newArr.splice(optIndex, 1, normalized); // Replace optimistic with real
            return newArr;
          }
        }

        // 3. Otherwise just add it
        return [...prev, normalized];
      });
    };
    socketService.on("new_dm_message", handleNewDm);

    return () => {
      socketService.off("new_dm_message", handleNewDm);
      socketService.emit("leave_dm", { userId: myId, otherUserId: otherId, incidentId, room });
      // Only clear messages if the modal is closing, NOT on every dependency change
      // (like incidentId loading) to prevent flickering/empty state.
      if (!isOpen) {
        setMessages([]);
        setRoomId(null);
      }
    };
  }, [isOpen, myId, otherId, buildRoomId, incidentId, passedRoomId]);

  const handleSend = async (attachment = null) => {
    const text = newMessage.trim();
    if (!text && !attachment) return;
    if (!roomId || sending) return;

    setSending(true);
    const optimisticId = `opt-${Date.now()}`;
    const optimistic = {
      id: optimisticId,
      senderId: myId,
      senderName: currentUser.name || currentUser.full_name || "You",
      receiverId: otherId,
      text,
      attachment,
      createdAt: new Date(),
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setNewMessage("");

    try {
      const displayName =
        currentUser.full_name ||
        currentUser.name ||
        currentUser.userName ||
        currentUser.email ||
        "";
      socketService.emit("send_dm_message", {
        senderId: String(myId),
        senderName: displayName,
        receiverId: String(otherId),
        incidentId: incidentId || undefined,
        room: roomId || undefined,
        text,
        attachment,
      });
    } catch (err) {
      console.error("DM send failed:", err);
      // Remove the optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !incidentId) return;

    // Check size (optional, but good practice)
    if (file.size > 20 * 1024 * 1024) {
      alert("File is too large. Max size is 20MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("files", file);

    try {
      const res = await axios.post(
        `${ApiEndPoint}/api/incidents/${incidentId}/chat/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      const fileData = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
      if (res.data.status && fileData) {
        // fileData expected: { url, filename, filetype, filesize }
        handleSend(fileData);
      } else {
        alert("Upload failed: " + (res.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Chat upload failed:", err);
      alert("Error uploading file. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const dateLabel = formatDate(msg.createdAt);
    if (!acc[dateLabel]) acc[dateLabel] = [];
    acc[dateLabel].push(msg);
    return acc;
  }, {});

  const targetName = targetUser?.name || targetUser?.full_name || targetUser?.email || "User";
  const targetInitial = targetName.charAt(0).toUpperCase();

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[100]"
          onClick={onClose}
        />
      )}

      {/* Slide-in Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-[420px] max-w-full bg-white shadow-2xl z-[110] flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-5 py-4 flex items-center gap-3 shrink-0">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center font-bold text-lg shrink-0">
            {targetInitial}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold truncate">{targetName}</h2>
            {incidentId && (
              <p className="text-xs text-slate-300 truncate flex items-center gap-1">
                <Lock size={10} />
                Private · {displayIncidentId || incidentId}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Privacy notice */}
        <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center gap-2">
          <Lock size={12} className="text-amber-600 shrink-0" />
          <p className="text-[11px] text-amber-700 font-medium">
            Private conversation — only you and {targetName} can see these messages
          </p>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-1 bg-gray-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
              <p className="text-sm">Loading messages…</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-gray-400">
              <MessageSquare size={40} className="text-gray-300" />
              <p className="text-sm text-center">
                No messages yet.<br />Start a private conversation with {targetName}.
              </p>
            </div>
          ) : (
            Object.entries(groupedMessages).map(([dateLabel, msgs]) => (
              <div key={dateLabel}>
                {/* Date separator */}
                <div className="flex items-center gap-2 my-3">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wide shrink-0">
                    {dateLabel}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>

                {msgs.map((msg) => {
                  const isMe = String(msg.senderId) === String(myId);
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isMe ? "justify-end" : "justify-start"} mb-1.5`}
                    >
                      {!isMe && (
                        <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-bold mr-2 mt-1 shrink-0">
                          {targetInitial}
                        </div>
                      )}
                      <div
                        className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${
                          isMe
                            ? "bg-indigo-600 text-white rounded-br-sm"
                            : "bg-white text-gray-800 rounded-bl-sm border border-gray-100"
                        } ${msg._optimistic ? "opacity-70" : ""}`}
                      >
                        {msg.attachment && (
                          <div className="mb-2 overflow-hidden rounded-lg">
                            {msg.attachment.filetype?.startsWith("image/") ? (
                              <AuthenticatedMediaLink href={msg.attachment.url} target="_blank" rel="noopener noreferrer">
                                <AuthenticatedMedia
                                  as="img"
                                  src={msg.attachment.url}
                                  alt={msg.attachment.filename}
                                  className="max-w-full h-auto object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                />
                              </AuthenticatedMediaLink>
                            ) : msg.attachment.filetype?.startsWith("video/") ? (
                              <AuthenticatedMedia
                                as="video"
                                src={msg.attachment.url}
                                controls
                                className="max-w-full h-auto rounded-lg"
                              />
                            ) : (
                              <AuthenticatedMediaLink
                                href={msg.attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium transition-colors ${
                                  isMe 
                                    ? "bg-indigo-500 hover:bg-indigo-400 text-white" 
                                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                                }`}
                              >
                                <FileText size={16} />
                                <span className="truncate max-w-[150px]">{msg.attachment.filename}</span>
                              </AuthenticatedMediaLink>
                            )}
                          </div>
                        )}
                        {msg.text && (
                          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {msg.text}
                          </p>
                        )}
                        <p
                          className={`text-[10px] mt-0.5 text-right ${
                            isMe ? "text-indigo-200" : "text-gray-400"
                          }`}
                        >
                          {formatTime(msg.createdAt)}
                          {msg._optimistic && " · sending…"}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-gray-200 bg-white px-3 py-3 shrink-0">
          <div className="flex items-end gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileUpload}
              accept="image/*,video/*,application/pdf"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending}
              className="p-2.5 rounded-full text-gray-500 hover:bg-gray-100 transition-all disabled:opacity-50"
              aria-label="Upload media"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Paperclip size={20} />
              )}
            </button>
            <textarea
              ref={inputRef}
              rows={1}
              className="flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:bg-white transition-all max-h-28 overflow-y-auto"
              placeholder={`Message ${targetName}…`}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              onInput={(e) => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 112) + "px";
              }}
            />
            <button
              onClick={() => handleSend()}
              disabled={(!newMessage.trim() && !uploading) || sending}
              className={`p-2.5 rounded-full shrink-0 transition-all ${
                (newMessage.trim() || uploading) && !sending
                  ? "bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-200"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivateChatModal;
